import React, { useState } from 'react';
import { X, Plus, Check, Users } from 'lucide-react';
import { useTranslation } from '../../../contexts/LanguageContext';

const AddMemberForm = ({ 
  friends = [],
  existingMemberEmails = [],
  onInvite, 
  onCancel,
  isProcessing
}) => {
  const { t } = useTranslation();
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [inviteEmails, setInviteEmails] = useState(['']);
  const [duplicateEmails, setDuplicateEmails] = useState([]);
  const [emailErrors, setEmailErrors] = useState({});

  const availableFriends = friends.filter(
    friend => !existingMemberEmails.includes(friend.email)
  );

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleFriendToggle = (friend) => {
    setSelectedFriends(prev => 
      prev.some(f => f.userId === friend.userId)
        ? prev.filter(f => f.userId !== friend.userId)
        : [...prev, friend]
    );
  };

  const handleEmailChange = (index, value) => {
    const newEmails = [...inviteEmails];
    newEmails[index] = value;
    setInviteEmails(newEmails);
    
    if (emailErrors[index]) {
      setEmailErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[index];
        return newErrors;
      });
    }
    
    if (duplicateEmails.includes(value)) {
      setDuplicateEmails(duplicateEmails.filter(email => email !== value));
    }
  };

  const addEmailField = () => setInviteEmails([...inviteEmails, '']);
  
  const removeEmailField = (index) => {
    if (inviteEmails.length > 1) {
      const newEmails = [...inviteEmails];
      const removedEmail = newEmails.splice(index, 1)[0];
      setInviteEmails(newEmails);
      
      if (emailErrors[index]) {
        setEmailErrors(prev => {
          const newErrors = {...prev};
          delete newErrors[index];
          return newErrors;
        });
      }
      
      if (duplicateEmails.includes(removedEmail)) {
        setDuplicateEmails(duplicateEmails.filter(email => email !== removedEmail));
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const errors = {};
    let hasErrors = false;
    
    inviteEmails.forEach((email, index) => {
      if (email.trim() && !isValidEmail(email)) {
        errors[index] = t('sharesubs.addMemberForm.invalidEmail');
        hasErrors = true;
      }
    });
    
    if (hasErrors) {
      setEmailErrors(errors);
      return;
    }
    
    const duplicates = inviteEmails.filter(
      email => email.includes('@') && existingMemberEmails.includes(email)
    );
    
    if (duplicates.length > 0) {
      setDuplicateEmails(duplicates);
      return;
    }
    
    const friendMembers = selectedFriends.map(friend => ({
      email: friend.email,
      userId: friend.userId,
      name: `${friend.firstName} ${friend.lastName}`
    }));
    
    const emailMembers = inviteEmails
      .filter(email => email.includes('@'))
      .map(email => {
        const friend = friends.find(f => f.email === email);
        return {
          email,
          userId: friend?.userId || null,
          name: friend ? `${friend.firstName} ${friend.lastName}` : email
        };
      });
    
    const allMembers = [...friendMembers, ...emailMembers];
    
    if (allMembers.length > 0) {
      onInvite(allMembers);
    }
  };

  return (
    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-md font-semibold text-gray-800">
          {t('sharesubs.addMemberForm.title')}
        </h3>
        <button 
          onClick={onCancel} 
          className="text-gray-500 hover:text-gray-700"
          disabled={isProcessing}
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            {t('sharesubs.addMemberForm.inviteFriends')}
          </label>
          
          {availableFriends.length === 0 ? (
            <div className="text-center py-2 text-gray-500 text-sm">
              {friends.length === 0 
                ? t('sharesubs.addMemberForm.noFriends') 
                : t('sharesubs.addMemberForm.allFriendsInSpace')}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto">
              {availableFriends.map(friend => (
                <div 
                  key={friend.userId}
                  onClick={() => handleFriendToggle(friend)}
                  className={`p-2 border rounded-lg cursor-pointer transition-colors ${
                    selectedFriends.some(f => f.userId === friend.userId)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center">
                    <div className="bg-gray-200 w-8 h-8 rounded-full flex items-center justify-center mr-2">
                      <Users className="h-4 w-4 text-gray-500" />
                    </div>
                    <div>
                      <div className="font-medium text-sm">{friend.firstName} {friend.lastName}</div>
                      <div className="text-xs text-gray-500">{friend.email}</div>
                    </div>
                    {selectedFriends.some(f => f.userId === friend.userId) && (
                      <Check className="h-4 w-4 text-blue-500 ml-auto" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('sharesubs.addMemberForm.inviteByEmail')}
          </label>
          
          {inviteEmails.map((email, index) => (
            <div key={index} className="mb-3">
              <div className="flex items-center">
                <div className="relative flex-1">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => handleEmailChange(index, e.target.value)}
                    className={`w-full p-2 text-sm border ${
                      duplicateEmails.includes(email) || emailErrors[index]
                        ? 'border-red-500'
                        : 'border-gray-300'
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    placeholder={t('sharesubs.addMemberForm.emailPlaceholder')}
                    disabled={isProcessing}
                  />
                </div>
                {inviteEmails.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeEmailField(index)}
                    className="ml-2 p-1 text-gray-500 hover:text-red-500"
                    disabled={isProcessing}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
                {index === inviteEmails.length - 1 && (
                  <button
                    type="button"
                    onClick={addEmailField}
                    className="ml-2 p-1 text-blue-500 hover:text-blue-700"
                    disabled={isProcessing}
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              {duplicateEmails.includes(email) && (
                <div className="text-xs text-red-500 mt-1">
                  {t('sharesubs.addMemberForm.alreadyInSpace')}
                </div>
              )}
              
              {emailErrors[index] && (
                <div className="text-xs text-red-500 mt-1">
                  {emailErrors[index]}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end space-x-2 mt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
            disabled={isProcessing}
          >
            {t('sharesubs.actions.cancel')}
          </button>
          <button
            type="submit"
            className={`px-3 py-1.5 text-sm rounded-lg text-white ${
              (selectedFriends.length === 0 && inviteEmails.every(email => !email.includes('@'))) || isProcessing
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-[#253985] hover:bg-[#1a2c6d]'
            }`}
            disabled={
              (selectedFriends.length === 0 && 
               inviteEmails.every(email => !email.includes('@'))) || 
              isProcessing
            }
          >
            {isProcessing ? t('sharesubs.addMemberForm.sendingInvites') : t('sharesubs.addMemberForm.inviteMembers')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddMemberForm;